import { useState, useEffect } from 'react';
import api, { surahApi, quranicProgressApi, studentApi } from '../lib/api';
import {
  Search,
  BookOpen,
  ChevronRight,
  CheckCircle2,
  Clock,
  RotateCcw,
  Circle,
  Filter,
  RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

export default function QuranProgressPage() {
  const [surahs, setSurahs] = useState([]);
  const [students, setStudents] = useState([]);
  const [progress, setProgress] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editDialog, setEditDialog] = useState({ open: false, surah: null });
  const [editForm, setEditForm] = useState({
    verses_memorized: 0,
    status: 'not_started',
    quality_score: 5,
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedStudent) {
      fetchProgress(selectedStudent);
    }
  }, [selectedStudent]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [surahsRes, studentsRes] = await Promise.all([
        surahApi.getAll(),
        studentApi.getAll({ student_type: 'quranic' }),
      ]);

      // If no surahs, seed them
      if (surahsRes.data.length === 0) {
        await surahApi.seed();
        const newSurahsRes = await surahApi.getAll();
        setSurahs(newSurahsRes.data);
      } else {
        setSurahs(surahsRes.data);
      }

      // Get franco-arabic and mixed students
      const mixedStudents = await studentApi.getAll({ student_type: 'mixed' });
      setStudents([...studentsRes.data, ...mixedStudents.data]);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async (studentId) => {
    try {
      const response = await quranicProgressApi.getAll({ student_id: studentId });
      setProgress(response.data);
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  const getProgressForSurah = (surahId) => {
    return progress.find((p) => p.surah_id === surahId);
  };

  const getStatusInfo = (status) => {
    const statuses = {
      not_started: { label: 'Non commencé', class: 'status-not-started', icon: Circle },
      in_progress: { label: 'En cours', class: 'status-in-progress', icon: Clock },
      acquired: { label: 'Acquis', class: 'status-acquired', icon: CheckCircle2 },
      revision: { label: 'Révision', class: 'status-revision', icon: RotateCcw },
    };
    return statuses[status] || statuses.not_started;
  };

  const handleOpenEdit = (surah) => {
    const existingProgress = getProgressForSurah(surah.id);
    setEditForm({
      verses_memorized: existingProgress?.verses_memorized || 0,
      status: existingProgress?.status || 'not_started',
      quality_score: existingProgress?.quality_score || 5,
      notes: existingProgress?.notes || '',
    });
    setEditDialog({ open: true, surah });
  };

  const handleSaveProgress = async () => {
    if (!selectedStudent || !editDialog.surah) return;

    try {
      const existingProgress = getProgressForSurah(editDialog.surah.id);
      
      if (existingProgress) {
        await quranicProgressApi.update(existingProgress.id, {
          ...editForm,
          student_id: selectedStudent,
          surah_id: editDialog.surah.id,
        });
      } else {
        await quranicProgressApi.create({
          ...editForm,
          student_id: selectedStudent,
          surah_id: editDialog.surah.id,
        });
      }

      toast.success('Progression mise à jour');
      fetchProgress(selectedStudent);
      setEditDialog({ open: false, surah: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  // Synchroniser la progression depuis les évaluations
  const handleSyncProgress = async () => {
    if (!selectedStudent) return;
    
    setSyncing(true);
    try {
      const response = await api.post(`/quranic-progress/sync-from-evals/${selectedStudent}`);
      toast.success(`${response.data.surahs_updated} sourate(s) synchronisée(s) depuis les évaluations`);
      fetchProgress(selectedStudent);
    } catch (error) {
      toast.error('Erreur lors de la synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const filteredSurahs = surahs.filter((surah) => {
    const matchesSearch =
      surah.name_french.toLowerCase().includes(searchQuery.toLowerCase()) ||
      surah.name_arabic.includes(searchQuery);
    
    if (statusFilter === 'all') return matchesSearch;
    
    const surahProgress = getProgressForSurah(surah.id);
    if (statusFilter === 'not_started') return matchesSearch && !surahProgress;
    return matchesSearch && surahProgress?.status === statusFilter;
  });

  // Calculate stats
  const calculateStats = () => {
    const total = surahs.length;
    const acquired = progress.filter((p) => p.status === 'acquired').length;
    const inProgress = progress.filter((p) => p.status === 'in_progress').length;
    const revision = progress.filter((p) => p.status === 'revision').length;
    return { total, acquired, inProgress, revision };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="quran-progress-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Progression Coranique</h1>
          <p className="text-muted-foreground text-sm">
            Suivez la mémorisation des sourates par élève
          </p>
        </div>
      </div>

      {/* Student Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Label className="text-sm font-medium mb-2 block">Sélectionner un élève</Label>
              <Select value={selectedStudent || ''} onValueChange={setSelectedStudent}>
                <SelectTrigger className="rounded-full" data-testid="select-student">
                  <SelectValue placeholder="Choisir un élève..." />
                </SelectTrigger>
                <SelectContent>
                  {students.map((student) => (
                    <SelectItem key={student.id} value={student.id}>
                      {student.first_name} {student.last_name} ({student.matricule})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedStudent && (
              <div className="flex items-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={handleSyncProgress}
                  disabled={syncing}
                  title="Synchroniser depuis les évaluations quotidiennes"
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? 'Sync...' : 'Sync Évals'}
                </Button>
                <Button variant="outline" asChild>
                  <a href={`/student-quran/${selectedStudent}`}>
                    <ChevronRight className="w-4 h-4 mr-1" />
                    Profil complet
                  </a>
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {selectedStudent && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="card-hover">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-2xl font-bold font-heading">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Total Sourates</p>
                </div>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold font-heading">{stats.acquired}</p>
                  <p className="text-xs text-muted-foreground">Acquises</p>
                </div>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold font-heading">{stats.inProgress}</p>
                  <p className="text-xs text-muted-foreground">En cours</p>
                </div>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <RotateCcw className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold font-heading">{stats.revision}</p>
                  <p className="text-xs text-muted-foreground">En révision</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher une sourate..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 rounded-full"
                    data-testid="search-surahs"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-48 rounded-full" data-testid="filter-status">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filtrer par statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="not_started">Non commencées</SelectItem>
                    <SelectItem value="in_progress">En cours</SelectItem>
                    <SelectItem value="acquired">Acquises</SelectItem>
                    <SelectItem value="revision">En révision</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Surahs Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSurahs.map((surah) => {
              const surahProgress = getProgressForSurah(surah.id);
              const statusInfo = getStatusInfo(surahProgress?.status);
              const StatusIcon = statusInfo.icon;
              const progressPercent = surahProgress
                ? Math.round((surahProgress.verses_memorized / surah.verses_count) * 100)
                : 0;

              return (
                <Card
                  key={surah.id}
                  className="card-hover cursor-pointer"
                  onClick={() => handleOpenEdit(surah)}
                  data-testid={`surah-card-${surah.number}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <span className="font-arabic text-lg text-primary font-bold">
                            {surah.number}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-medium text-foreground">{surah.name_french}</h3>
                          <p className="font-arabic text-sm text-muted-foreground">
                            {surah.name_arabic}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className={cn('text-xs', statusInfo.class)}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusInfo.label}
                      </Badge>
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          {surahProgress?.verses_memorized || 0} / {surah.verses_count} versets
                        </span>
                        <span className="font-medium text-foreground">{progressPercent}%</span>
                      </div>
                      <Progress value={progressPercent} className="h-2" />
                    </div>

                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                      <span className="text-xs text-muted-foreground">Juz {surah.juz}</span>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {!selectedStudent && (
        <Card>
          <CardContent className="py-12 text-center">
            <BookOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-muted-foreground">
              Sélectionnez un élève pour voir sa progression coranique
            </p>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ ...editDialog, open })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editDialog.surah?.name_french}
              <span className="font-arabic text-muted-foreground ml-2">
                {editDialog.surah?.name_arabic}
              </span>
            </DialogTitle>
            <DialogDescription>
              Mettre à jour la progression ({editDialog.surah?.verses_count} versets)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="space-y-3">
              <Label>Versets mémorisés</Label>
              <div className="flex items-center gap-4">
                <Slider
                  value={[editForm.verses_memorized]}
                  onValueChange={([value]) => setEditForm({ ...editForm, verses_memorized: value })}
                  max={editDialog.surah?.verses_count || 0}
                  step={1}
                  className="flex-1"
                  data-testid="verses-slider"
                />
                <span className="text-sm font-medium w-16 text-right">
                  {editForm.verses_memorized} / {editDialog.surah?.verses_count}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Statut</Label>
              <Select
                value={editForm.status}
                onValueChange={(value) => setEditForm({ ...editForm, status: value })}
              >
                <SelectTrigger data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_started">Non commencé</SelectItem>
                  <SelectItem value="in_progress">En cours</SelectItem>
                  <SelectItem value="acquired">Acquis</SelectItem>
                  <SelectItem value="revision">En révision</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <Label>Qualité de récitation (1-10)</Label>
              <div className="flex items-center gap-4">
                <Slider
                  value={[editForm.quality_score]}
                  onValueChange={([value]) => setEditForm({ ...editForm, quality_score: value })}
                  min={1}
                  max={10}
                  step={1}
                  className="flex-1"
                  data-testid="quality-slider"
                />
                <span className="text-sm font-medium w-8 text-right">
                  {editForm.quality_score}/10
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                placeholder="Observations..."
                data-testid="notes-input"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setEditDialog({ open: false, surah: null })}
            >
              Annuler
            </Button>
            <Button onClick={handleSaveProgress} data-testid="save-progress-btn">
              Enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
